import { basicSetup } from 'codemirror';
import { json, jsonParseLinter } from '@codemirror/lang-json';
import { linter } from '@codemirror/lint';
import { EditorSelection, EditorState, StateEffect, StateField } from '@codemirror/state';
import { Decoration, EditorView, WidgetType } from '@codemirror/view';

const setCollapsedRanges = StateEffect.define();

class EllipsisWidget extends WidgetType {
  toDOM() {
    const element = document.createElement('span');
    element.className = 'cm-json-ellipsis';
    element.textContent = '...';
    return element;
  }

  eq() {
    return true;
  }
}

const ellipsisWidget = new EllipsisWidget();
const collapsedRanges = StateField.define({
  create() {
    return Decoration.none;
  },
  update(decorations, transaction) {
    const effect = transaction.effects.find((item) => item.is(setCollapsedRanges));
    if (effect) return createCollapsedDecorations(effect.value);
    return transaction.docChanged ? Decoration.none : decorations.map(transaction.changes);
  },
  provide: (field) => EditorView.decorations.from(field),
});

function createCollapsedDecorations(ranges) {
  return Decoration.set(
    ranges.map(({ from, to }) => Decoration.replace({ widget: ellipsisWidget }).range(from, to)),
    true,
  );
}

const editorTheme = EditorView.theme({
  '&': { height: '100%', fontSize: '14px' },
  '.cm-scroller': { fontFamily: "'SFMono-Regular', Consolas, 'Liberation Mono', monospace" },
  '.cm-content': { padding: '14px 0' },
  '.cm-gutters': { backgroundColor: '#f8fafc', borderRight: '1px solid #e2e8f0' },
  '&.cm-focused': { outline: 'none' },
});

export class JsonEditor {
  constructor(parent, { value = '', onChange = () => {} } = {}) {
    this.collapsedRangeData = [];
    this.view = new EditorView({
      parent,
      state: EditorState.create({
        doc: value,
        extensions: [
          basicSetup,
          json(),
          linter(jsonParseLinter()),
          EditorView.lineWrapping,
          editorTheme,
          collapsedRanges,
          EditorView.updateListener.of((update) => {
            if (update.docChanged) {
              this.collapsedRangeData = [];
              onChange(update.state.doc.toString());
            }
          }),
        ],
      }),
    });
  }

  getValue() {
    return this.view.state.doc.toString();
  }

  getCursorPosition() {
    return this.view.state.selection.main.head;
  }

  collapseRanges(ranges) {
    const nextRanges = [
      ...this.collapsedRangeData.filter((existing) => !ranges.some((range) => rangesOverlap(existing, range))),
      ...ranges,
    ].sort((left, right) => left.from - right.from);
    this.setCollapsedRanges(nextRanges);
  }

  expandRanges(ranges) {
    const rangeKeys = new Set(ranges.map(rangeKey));
    const nextRanges = this.collapsedRangeData.filter((range) => !rangeKeys.has(rangeKey(range)));
    const changed = nextRanges.length !== this.collapsedRangeData.length;
    if (changed) this.setCollapsedRanges(nextRanges);
    return changed;
  }

  setCollapsedRanges(ranges) {
    this.collapsedRangeData = ranges;
    this.view.dispatch({ effects: setCollapsedRanges.of(ranges) });
  }

  setValue(value) {
    this.view.dispatch({
      changes: { from: 0, to: this.view.state.doc.length, insert: value },
      selection: EditorSelection.cursor(0),
    });
  }

  focus() {
    this.view.focus();
  }

  selectRange(from, to = from) {
    const max = this.view.state.doc.length;
    const safeFrom = Math.min(Math.max(from, 0), max);
    const safeTo = Math.min(Math.max(to, safeFrom), max);
    this.view.dispatch({
      selection: EditorSelection.range(safeFrom, safeTo),
      effects: EditorView.scrollIntoView(safeFrom, { y: 'center' }),
    });
    this.focus();
  }
}

function rangeKey({ from, to }) {
  return `${from}:${to}`;
}

function rangesOverlap(left, right) {
  return left.from < right.to && right.from < left.to;
}
